import React, { useCallback } from "react";
import { Hint } from "@progress/kendo-react-labels";
import { EXPRESSION_LABELS, FIELD_TYPE_HINTS } from "../../../constants/expressionBuilder.constants";
import "../../../styles/ExpressionBuilder.css";
import ComboBoxInput from "./ComboBoxInput/ComboBoxInput";
import DateInput from "./DateInput/DateInput";
import DisabledInput from "./DisabledInput/DisabledInput";
import NumberInput from "./NumberInput/NumberInput";
import TextInput from "./TextInput/TextInput";

// --- Input Component Map (Replacing the Switch Statement) ---
const INPUT_COMPONENTS = {
  number: NumberInput,
  dropdown: ComboBoxInput,
  text: ComboBoxInput, // Using ComboBox for text fields to allow variable selection
  date: DateInput,
  // default: TextInput, // The main component's logic will handle the default
};

/**
 * Component for value input in expression rules
 * @param {string} fieldType - Type of the selected field
 * @param {*} value - Current value
 * @param {Function} onValueChange - Value change handler
 * @param {Object} selectedField - Selected field object
 * @param {boolean} showHint - Whether to show value hint
 * @param {Object} selectedOperator - Currently selected operator
 * @param {Array} availableFields - Available field options
 * @returns {JSX.Element} Value input component
 * @author Abhishek Dhillon
 * @parent RuleComponent
 */
const ValueInput = ({ fieldType, value, onValueChange, selectedField, showHint = true, selectedOperator, availableFields = [] }) => {
  /**
   * Consistent change handler for simple inputs (Input, NumericTextBox)
   * @param {Object} e - Event object
   * @author Abhishek Dhillon
   */
  const handleSimpleChange = useCallback(
    (e) => {
      onValueChange(e.target.value);
    },
    [onValueChange]
  );

  /**
   * Check for standard function outside renderInput for clarity
   * @returns {boolean} True if operator is a standard function
   * @author Abhishek Dhillon
   */
  const isStandardFunction = () => {
    const operatorId = selectedOperator?.id || selectedOperator;
    const standardFunctions = ["IS_BLANK", "IS_NOT_BLANK", "IS_ZERO", "IS_NOT_ZERO"];
    return standardFunctions.includes(operatorId);
  };

  /**
   * Renders the appropriate input component based on field type
   * @returns {JSX.Element} Input component
   * @author Abhishek Dhillon
   */
  const renderInput = () => {
    if (isStandardFunction()) {
      return <DisabledInput />;
    }

    const commonProps = {
      value: value || "",
      onChange: handleSimpleChange,
      onValueChange: onValueChange, // Passed explicitly for ComboBox to use
      className: "expression-builder-w-100",
      placeholder: EXPRESSION_LABELS.ENTER_VALUE,
      availableFields: availableFields,
      selectedField: selectedField,
    };

    // Get Component from map, fallback to TextInput for unknown types
    const InputComponent = INPUT_COMPONENTS[fieldType] || TextInput;

    // Render the selected component
    return <InputComponent {...commonProps} />;
  };

  /**
   * Gets hint text for the selected field type based on value selection
   * @returns {string} Hint text
   * @author Abhishek Dhillon
   */
  const getFieldHint = () => {
    // Check if current value is a custom value or dropdown selection
    const isCustomValue = value?.custom === true;
    const isDropdownSelection = value?.custom === false;
    if (isCustomValue) {
      return `Custom value selected.`;
    } else if (isDropdownSelection) {
      return `Dropdown value selected: "${value?.name || value?.value}".`;
    }
  };

  return (
    <div className='expression-builder-rule-value'>
      <label>{EXPRESSION_LABELS.VALUE}</label>
      {renderInput()}
      {showHint && selectedField && !isStandardFunction() && (
        <div className='expression-builder-rule-value-hint'>
          <Hint>{getFieldHint()}</Hint>
        </div>
      )}
    </div>
  );
};

export default ValueInput;
